import { z } from 'zod';

const timeRegex = /^([0-1]?\d|2[0-3]):[0-5]\d$|^24:00$/;

export const ScheduleSchema = z
  .object({
    id: z.number(),
    name: z
      .string()
      .min(1, 'Название обязательно')
      .max(100, 'Максимум 100 символов'),
    description: z.string().optional(),
    periodType: z.number().min(1, 'Тип периода обязателен'),
    periodValue: z.number().min(1, 'Значение периода обязательно'),
    scheduleSettings: z.array(
      z.object({
        id: z.number(),
        dayNumber: z.number().min(1, 'День обязателен'),
        weekNumber: z.number().min(1, 'Неделя обязательна'),
        workdayStart: z
          .string()
          .min(1, 'Начало рабочего дня обязательно')
          .regex(timeRegex, 'Формат ЧЧ:ММ'),
        workdayDuration: z
          .string()
          .min(1, 'Продолжительность рабочего дня обязательна')
          .regex(timeRegex, 'Формат ЧЧ:ММ'),
        breakDuration: z
          .string()
          .optional()
          .refine((value) => !value || timeRegex.test(value), 'Формат ЧЧ:ММ'),
        guid: z.string().optional()
      })
    )
  })
  .superRefine((data, ctx) => {
    const seen = new Set<string>();
    const duplicates = new Map<number, string>();

    data.scheduleSettings.forEach((setting, index) => {
      const key = `${setting.dayNumber}-${setting.weekNumber}`;

      if (seen.has(key)) {
        duplicates.set(index, key);
      } else {
        seen.add(key);
      }
    });

    duplicates.forEach((key, index) => {
      const [day, week] = key.split('-');
      const message = `День ${day} недели ${week} уже существует в расписании`;

      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message,
        path: ['scheduleSettings', index, 'dayNumber']
      });

      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message,
        path: ['scheduleSettings', index, 'weekNumber']
      });
    });
  });

export const ScheduleWithDatesSchema = z
  .object({
    id: z.number(),
    name: z.string().optional(),
    scheduleId: z.number().min(1, 'Обязательное поле'),
    startDate: z.string().nonempty('Укажите дату начала'),
    endDate: z.string().nonempty('Укажите дату окончания')
  })
  .superRefine((schedule, ctx) => {
    if (!schedule.startDate || !schedule.endDate) return;

    const start = new Date(schedule.startDate);
    const end = new Date(schedule.endDate);

    if (start > end) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Дата начала не может быть позже даты окончания',
        path: ['generalDatesErrors', 'startDate']
      });
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Дата окончания не может быть ранее даты начала',
        path: ['generalDatesErrors', 'endDate']
      });
    } else if (start.getTime() === end.getTime()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Дата начала и окончания не могут совпадать',
        path: ['generalDatesErrors', 'startDate']
      });
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Дата начала и окончания не могут совпадать',
        path: ['generalDatesErrors', 'endDate']
      });
    }
  });
